'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useProductImages } from '@/hooks/useProductImages';
import { useStore } from '@/providers/app-store-provider';
import { EProductImageType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { postNotifyProduct } from '@/api/product';
import Wish from '@/components/Product/Wish';
import { toast } from '@/components/Toast';
import { useFrenchSummerTracking } from '../hooks/useFrenchSummerTracking';

import { EPlatform } from '@/utils/enums/platform-enum';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import appCommunicationManager from '@/utils/app-communication-manager';
import { randomFuncName } from '@/utils/tools';
import logger from '@/utils/client-logger';
// @ts-ignore
import { AlarmOutlined, CheckOutlined } from '@zeelool/icon';

// 商品数据接口
interface GoodItemProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
    product_gray?: string[];
  };
  price_info?: any[];
  promo_listed_status?: number;
  [key: string]: any;
}

interface GoodItemPcProps {
  product: GoodItemProduct;
  index: number;
  tabName?: string;
  moduleName?: string;
  deviceType?: EPlatform;
  onProductClick?: (product: GoodItemProduct, index: number) => void;
  onProductExp?: (product: GoodItemProduct, index: number) => void;
}

export default function GoodItemPc({
  product,
  index,
  tabName = '',
  moduleName = '商品版位',
  deviceType = EPlatform.WEB,
  onProductClick,
  onProductExp,
}: GoodItemPcProps) {
  const { navigate } = useAppNavigation();
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const summerT = useTranslations('activity.summerEdit' as any) as any;

  // hover状态管理
  const [isHovered, setIsHovered] = useState(false);
  const [localPromoStatus, setLocalPromoStatus] = useState<number>(
    product.promo_listed_status || 3,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setShowLoginRegisterDialog = useStore((s) => s.setShowLoginRegisterDialog);
  const setToken = useStore((s) => s.setToken);
  const refreshAccountInfo = useStore((s) => s.refreshAccountInfo);

  useEffect(() => {
    setLocalPromoStatus(product.promo_listed_status || 3);
  }, [product.promo_listed_status]);

  // 使用 useProductImages hook 获取商品图片
  const { productImage } = useProductImages({
    productData: product as any,
    imageRule: [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY],
  });

  // 预售商品卡片 hover 时的图片切换规则：红人图 > 素材图 > 商品二图
  const thirdImage =
    (product.image as any)?.celebrity?.[0] ||
    (product.image as any)?.material?.[0] ||
    product.image?.product_gray?.[2] ||
    product.image?.product?.[2] ||
    productImage.src;

  // 是否存在红人图或素材图
  const hasCelebrityImage =
    (product.image as any)?.celebrity?.[0] || (product.image as any)?.material?.[0];

  // 曝光检测
  const { ref: expRef, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onProductExp?.(product, index);
    }
  }, [inView, onProductExp, product, index]);

  // 判断是否售罄或下架 (status === 2)
  const isSoldOut = product.stock <= 0;
  const isNoRestock = product.status === 2;

  const { trackNotifyMeClick, trackNotifyMeSubmit } = useFrenchSummerTracking();
  const [pendingSubscribe, setPendingSubscribe] = useState(false);

  useEffect(() => {
    // 当用户信息更新，且存在待订阅状态时，自动触发订阅
    if (userInfo?.email && pendingSubscribe) {
      const autoSubscribe = async () => {
        try {
          setIsSubmitting(true);
          await postNotifyProduct({
            email: userInfo.email,
            id: product.id as any, // 使用 id 而不是 business_sku
            notice_type: 'promo_listed',
          });
          setLocalPromoStatus(2); // 成功后更新状态为已订阅
          trackNotifyMeSubmit(product, moduleName, index, tabName);
        } catch {
          // 错误已经在 request 里处理
        } finally {
          setIsSubmitting(false);
          setPendingSubscribe(false);
        }
      };
      autoSubscribe();
    }
  }, [userInfo?.email, pendingSubscribe, product, trackNotifyMeSubmit, index, tabName, moduleName]);

  const handleNotifyMe = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    trackNotifyMeClick(product, moduleName, index, tabName);

    if (isSubmitting) return;

    if (!userInfo?.email) {
      setPendingSubscribe(true);
      if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
        try {
          const logincallbackFnName = randomFuncName('JumpToPage');
          (window as any)[logincallbackFnName] = async (val: any) => {
            if (val !== 'ClearToken') {
              setToken(val);
              setTimeout(async () => {
                await refreshAccountInfo();
              }, 100);
            }
          };
          const loginData = {
            RouteName: '/login',
            closedCurrentPage: false,
            backToken: true,
            Callback: logincallbackFnName,
          };
          appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, loginData);
        } catch (error) {
          logger.error('App登录跳转失败:', error);
        }
      } else {
        setShowLoginRegisterDialog(true);
      }
      return;
    }

    try {
      setIsSubmitting(true);
      await postNotifyProduct({
        email: userInfo.email,
        id: product.id as any, // 使用 id 而不是 business_sku
        notice_type: 'promo_listed',
      });
      setLocalPromoStatus(2); // 成功后更新状态为已订阅
      trackNotifyMeSubmit(product, moduleName, index, tabName);
    } catch {
      // 错误已经在 request 里处理
    } finally {
      setIsSubmitting(false);
    }
  };

  // 处理商品点击
  const toDetail = (item: GoodItemProduct, itemIndex: number) => {
    onProductClick?.(item, itemIndex);

    // 跳转到商品详情页
    if (item?.shop_sku) {
      navigate(`/goods-detail/${item.shop_sku}`);
    }
  };

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toDetail(product, index);
        if (product.shop_sku) {
          if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
            appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, {
              RouteName: `/goods-detail/${product.shop_sku}`,
              closedCurrentPage: false,
            });
          } else {
            window.location.href = `/goods-detail/${product.shop_sku}`;
          }
        }
      }}
      className="cursor-pointer"
    >
      <div
        ref={expRef}
        className="rounded-[12px] overflow-hidden bg-[#eeeeee] flex flex-col relative"
        style={{ aspectRatio: '413/574' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Coming Soon Badge */}
        {(localPromoStatus === 1 || localPromoStatus === 2) && (
          <div className="absolute top-[20px] left-[12px] z-10 flex pointer-events-none rounded-[12px] bg-white/40 py-[3px] px-[6px]">
            <span className="font-bs text-[12px] text-[#282828] leading-none">
              {(summerT as any)('coomingSoon')}
            </span>
          </div>
        )}

        {/* Wishlist Icon */}
        <Wish
          data={product as any}
          className="absolute top-[20px] right-[13px] z-10 flex"
          iconSize="18px"
        />

        <div
          className={`img-wrap flex-1 flex items-center justify-center w-full overflow-hidden relative ${hasCelebrityImage && isHovered ? '' : 'px-[22px]'}`}
        >
          {/* 主图片 */}
          <Image
            src={isHovered ? thirdImage : productImage.src}
            alt={`${product.name || ''}`}
            width={500}
            height={500}
            className={`transition-opacity duration-300 ${
              !productImage.isGray && productImage.type === EProductImageType.PRODUCT
                ? 'mix-blend-multiply'
                : ''
            } ${
              hasCelebrityImage && isHovered
                ? 'h-full object-cover'
                : 'w-full h-auto max-w-[24.72vw] max-h-full object-contain'
            } ${product.stock <= 0 ? 'opacity-50' : ''}`}
          />
        </div>
        <div className="price-info flex flex-col justify-end items-start mt-auto w-full p-[24px]">
          {/* Notify Me / Subscribed Button */}
          {(localPromoStatus === 1 || localPromoStatus === 2) && isHovered && (
            <div className="absolute bottom-[90px] left-[0] z-10 w-full mb-[20px] px-[24px]">
              {localPromoStatus === 1 ? (
                <button
                  className="w-full h-[44px] bg-white text-[#282828] font-bb text-[16px] rounded-[4px] flex items-center justify-center gap-[4px] cursor-pointer"
                  onClick={handleNotifyMe}
                >
                  <AlarmOutlined className="text-[24px]" />
                  {(summerT as any)('notify')}
                </button>
              ) : (
                <button
                  className="w-full h-[44px] bg-white text-[#282828]/50 font-bb text-[16px] rounded-[4px] flex items-center justify-center gap-[4px] cursor-not-allowed"
                  onClick={(e) => {
                    toast.info((summerT as any)('subscribedToast'));
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <CheckOutlined className="text-[24px]" />
                  {(summerT as any)('subscribed')}
                </button>
              )}
            </div>
          )}

          <p
            className="text-left text-[#282828] mb-[8px] line-clamp-1 font-br min-h-[19px]"
            style={{
              fontSize: '16px',
              lineHeight: '19px',
            }}
          >
            {product.short_name}
          </p>
          <div className="price flex items-center justify-start w-full relative h-[19px]">
            <div className="flex items-center justify-start">
              {(() => {
                const p = priceInfo(userInfo, product, currency) || {};
                const now = formatPriceNumberWithSymbol(
                  String(p?.sell_price || p?.price || '0'),
                  currency,
                );
                const old =
                  p && p.price && p.sell_price && p.sell_price !== p.price
                    ? formatPriceNumberWithSymbol(String(p.price), currency)
                    : '';
                return (
                  <>
                    <span
                      className="now-price notranslate text-[#282828] font-bb"
                      style={{
                        fontSize: '16px',
                        lineHeight: '19px',
                      }}
                    >
                      {now}
                    </span>
                    {old && (
                      <span
                        className="old-price notranslate ml-[8px] text-[#282828] line-through font-br"
                        style={{
                          fontSize: '16px',
                          lineHeight: '19px',
                        }}
                      >
                        {old}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* 售罄/下架遮罩 (仅在 promoStatus === 3 或没有该字段时显示) */}
        {(isSoldOut || isNoRestock) &&
          (!product.promo_listed_status || product.promo_listed_status === 3) && (
            <div className="absolute inset-0 bg-[rgba(245,245,245,0.5)] z-20">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[100px] h-[100px] rounded-full bg-[rgba(0,0,0,0.5)] text-center text-white text-[14px]">
                {isNoRestock ? 'No Restock' : 'Sold Out'}
              </span>
            </div>
          )}
      </div>
    </div>
  );
}
